import React, { useMemo, useState } from "react";
import { Box, Dialog, DialogTitle, IconButton, Stack } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SubscriptionCard from "../../../../common/components/subscription-card/subscription-card";
import { FormikErrors, FormikValues, useFormikContext } from "formik";
import { CompanyFormValues } from "../add-new-tenet-helper";
import LeftEllipses from "../../../../assets/icons/left-tenet-bg.svg";
import RightEllipses from "../../../../assets/icons/right-tenant-ellipse.svg";
import CustomPlanCard from "../../../../common/components/subscription-card/custom-plan-card";
import CreateCustomPlan from "../../crm-plans/crm-custom-plans/create-custom-plan/create-custom-plan";
import { initialValues } from "../../crm-plans/crm-plans-utils";
import { getSubscriptionPlans } from "../../../../api/tenets";
import { useSetLoading } from "../../../../common/hooks/appearance";
import { AlertComponent } from "../../../../common/components";
import { AlertType } from "../../../../utils/util-types";
import { ALERT_TYPE } from "../../../../utils/constants";

export const bgColors = ["#D59F56", "#CD5C5C", "#4B3B31", "#004C4C"];

interface SubscriptionPlansProps {
  values: CompanyFormValues | { subscriptionPlanId: any };
  errors: FormikErrors<FormikValues>;
  subscriptionData: any;
  showBgImages?: boolean;
  getSubscriptionValues?: () => Promise<void>;
}

const SubscriptionPlans: React.FC<SubscriptionPlansProps> = ({
  values,
  errors,
  subscriptionData,
  showBgImages = true,
  getSubscriptionValues,
}) => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const [createCustomPlanOpen, setCreateCustomPlanOpen] = useState(false);
  const [newlyCreatedPlan, setNewlyCreatedPlan] = useState(null);
  const [planInitialValues, setPlanInitialValues] =
    useState<typeof initialValues>(initialValues);
  const [isLoading, setIsLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState<AlertType>(ALERT_TYPE.SUCCESS);

  const { setFieldValue } = useFormikContext<{ [key: string]: any }>();

  const tabs = ["Billed Monthly", "Billed Annually", "No Cost"];

  const handleOnClcik = (item: any) => {
    setFieldValue("subscriptionPlanId", item);
  };
  let bgImage = {};
  if (showBgImages) {
    bgImage = {
      backgroundImage: `url(${RightEllipses}), url(${LeftEllipses})`,
      backgroundRepeat: "no-repeat, no-repeat",
      backgroundPosition: "top left, bottom right",
    };
  }

  const showLoading = useMemo(() => isLoading, [isLoading]);
  useSetLoading(showLoading);

  return (
    <Box style={bgImage} sx={{ width: "100%" }}>
      <Box className="subscription-tabs-wrapper">
        <Box className="subscription-tabs-container" gap={3}>
          {tabs?.map((tab: string, index: number) => (
            <span
              className={index === activeTab ? "active" : ""}
              onClick={() => setActiveTab(index)}
            >
              {" "}
              {tab}
            </span>
          ))}
        </Box>
      </Box>
      {activeTab === 0 && (
        <Stack
          spacing={{ xs: 1, sm: 2, md: 4 }}
          direction="row"
          useFlexGap
          sx={{ flexWrap: "wrap", justifyContent: "center" }}
        >
          {subscriptionData?.monthly?.map((item: any, idx: number) => (
            <SubscriptionCard
              key={item.id}
              item={item}
              onClick={handleOnClcik}
              selectedItem={values?.subscriptionPlanId}
              bgColor={bgColors[idx % bgColors.length]}
            />
          ))}
          {subscriptionData?.customMonthly?.length > 0 && (
            <CustomPlanCard
              plans={subscriptionData.customMonthly}
              selectedPlan={values?.subscriptionPlanId}
              onChoosePlan={(plan: any) => {
                setFieldValue("subscriptionPlanId", plan);
              }}
              onCreate={() => setCreateCustomPlanOpen(true)}
            />
          )}
        </Stack>
      )}
      {activeTab === 1 && (
        <Stack
          spacing={{ xs: 1, sm: 2, md: 4 }}
          direction="row"
          useFlexGap
          sx={{ flexWrap: "wrap", justifyContent: "center" }}
        >
          {subscriptionData?.annually?.map((item: any, idx: number) => (
            <SubscriptionCard
              key={item.id}
              item={item}
              onClick={handleOnClcik}
              selectedItem={values?.subscriptionPlanId}
              bgColor={bgColors[idx % bgColors.length]}
            />
          ))}

          {subscriptionData?.customAnnual?.length > 0 && (
            <CustomPlanCard
              plans={subscriptionData.customAnnual}
              selectedPlan={values?.subscriptionPlanId}
              onChoosePlan={(plan: any) => {
                setFieldValue("subscriptionPlanId", plan);
              }}
              onCreate={() => setCreateCustomPlanOpen(true)}
            />
          )}
        </Stack>
      )}
      {activeTab === 2 && (
        <Stack
          spacing={{ xs: 1, sm: 2, md: 4 }}
          direction="row"
          useFlexGap
          sx={{ flexWrap: "wrap", justifyContent: "center" }}
        >
          {subscriptionData?.noCost?.length ? (
            subscriptionData?.noCost?.map((item: any, index: number) => (
              <SubscriptionCard
                key={item.id}
                item={item}
                onClick={handleOnClcik}
                selectedItem={values?.subscriptionPlanId || ""}
                bgColor={bgColors[index % bgColors.length]}
              />
            ))
          ) : (
            <Box>No Plans Available</Box>
          )}
        </Stack>
      )}
      <Dialog
        open={createCustomPlanOpen}
        onClose={() => setCreateCustomPlanOpen(false)}
        fullWidth
        maxWidth="lg"
        slotProps={{
          paper: {
            sx: {
              backgroundColor: "#f1f1f1",
            },
          },
        }}
      >
        <DialogTitle sx={{ m: 0, p: 2 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>Create Custom Plan</span>

            <IconButton
              aria-label="close"
              onClick={() => setCreateCustomPlanOpen(false)}
              sx={{
                color: "#555",
                "&:hover": { color: "black" },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <CreateCustomPlan
          initialValues={planInitialValues}
          setIsLoading={setIsLoading}
          setAlertOpen={setAlertOpen}
          setAlertMessage={setAlertMessage}
          setAlertType={setAlertType}
          alertMessage={alertMessage}
          alertType={alertType}
          alertOpen={alertOpen}
          setCreateCustomPlanOpen={setCreateCustomPlanOpen}
          fetchPlans={async () => {
            const updatedPlans = await getSubscriptionPlans();
            const latest = updatedPlans.data;
            setNewlyCreatedPlan(latest);
            await getSubscriptionValues?.();
          }}
        />
      </Dialog>
    </Box>
  );
};

export default SubscriptionPlans;
